import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useWorldFeatures } from "@/lib/useWorldFeatures";
import { colorFor } from "@/lib/countryColors";
import { featureKey } from "@/lib/mapGeometry";
import { VIEW_W, VIEW_H, MapDefs, Sea, Compass, Legend, CalloutPin, ZoomControls, MemoryDot } from "@/components/map/MapParts";
import { useMediaCounts } from "@/lib/useMediaCounts";
import { useAtlasGeometry } from "@/components/map/useAtlasGeometry";
import { useMapZoom } from "@/components/map/useMapZoom";

const SHAPE_SELECTED = { opacity: 1 };
const SHAPE_IDLE = { opacity: 0.95 };
const HOVER = { opacity: 1 };
const POINTER = { cursor: "pointer" };

function CountryShape({ shape, country, selected, onSelect, k }) {
  return (
    <motion.path
      d={shape.d}
      fill={country ? `url(#flag-${shape.code})` : colorFor(shape.code)}
      stroke={selected ? "#2B2726" : "#FFFFFF"}
      strokeWidth={(selected ? 1.8 : 0.8) / k}
      initial={false}
      animate={selected ? SHAPE_SELECTED : SHAPE_IDLE}
      whileHover={HOVER}
      style={POINTER}
      onClick={() => onSelect(shape.code)}
      data-testid={`country-shape-${shape.code}`}
    />
  );
}

/** D3 map of the Arab world: every country filled with its flag, the selected one highlighted in place. Pinch/buttons to zoom. */
export default function AtlasMap({ countries, onSelect, selectedCode }) {
  const { t } = useLang();
  const features = useWorldFeatures();
  const { pathGen, arabShapes, nonArabFeatures, disputedShapes, pins, codeToCountry, project } = useAtlasGeometry(features, countries);
  const memories = useMediaCounts(countries, project);
  const { svgRef, transform, zoomIn, zoomOut, reset } = useMapZoom();
  const k = transform.k;

  return (
    <div className="relative w-full h-full grain" data-testid="atlas-map">
      <svg ref={svgRef} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="w-full h-auto select-none" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Arab world map">
        <MapDefs shapes={arabShapes} codeToCountry={codeToCountry} />
        <Sea />
        <g transform={transform.toString()} data-testid="map-viewport">
          <g>
            {nonArabFeatures.map((f) => (
              <path key={featureKey("nonarab", f)} d={pathGen(f)} fill="#E9E1D3" stroke="#CDBFA3" strokeWidth={0.3 / k} opacity="0.4" />
            ))}
          </g>
          <g>
            {arabShapes.map((s) => (
              <CountryShape key={s.code} shape={s} country={codeToCountry[s.code]} selected={selectedCode === s.code} onSelect={onSelect} k={k} />
            ))}
            {disputedShapes.map((s) => (
              <path key={`hatch-${s.code}`} d={s.d} fill="url(#hatch-disputed)" stroke="#7A0A15" strokeWidth={(selectedCode === s.code ? 1.6 : 0.8) / k} pointerEvents="none" opacity="0.9" />
            ))}
          </g>
          {pins.map((c) => (
            <CalloutPin key={c.code} country={c} selected={selectedCode === c.code} onSelect={onSelect} k={k} />
          ))}
          {memories.map((m) => (
            <MemoryDot key={m.code} dot={m} label={t("memories_tooltip").replace("{n}", m.count)} onSelect={onSelect} k={k} />
          ))}
        </g>
        <Compass />
      </svg>
      <ZoomControls k={k} zoomIn={zoomIn} zoomOut={zoomOut} reset={reset} />
      <Legend t={t} memories={memories.length > 0} />
    </div>
  );
}
